import { useCallback, useEffect, useRef } from "react";
import { client } from "@/utils/uno-axios";
import { calcUrl } from "@/features/ui/helpers/fetcher-helpers";

// CARD = 카드 보기(갤러리), DETAIL = 자세히 보기(편집 그리드)
export type OrderSheetView = "CARD" | "DETAIL";
// OPEN = 주문 화면을 열었을 때 켜져 있던 보기, SWITCH = 버튼으로 바꾼 보기
export type OrderSheetViewTrigger = "OPEN" | "SWITCH";

export const logOrderSheetViewTemplateUrl =
  "/buyers/:buyerId/order-sheet-sets/:orderSheetSetId/view-logs";

interface UseLogOrderSheetViewParameter {
  // 바이어가 아니면 undefined — 기록하지 않는다
  buyerId: number | undefined;
  orderSheetSetId: number;
  view: OrderSheetView;
  // 주문서를 실제로 불러온 뒤에만 '열었다'고 남긴다(없는 주문서·권한 없는 주문서는 빼려고)
  isEnabled: boolean;
}

/**
 * 바이어가 주문 화면을 어떤 보기로 쓰는지 서버(user_action_log)에 남긴다.
 *
 * 고른 보기는 브라우저(localStorage)에만 저장돼 서버에서는 알 길이 없었다.
 * - 주문서를 열 때 그때 켜져 있던 보기를 한 번(OPEN)
 * - 보기 버튼으로 바꿀 때마다(SWITCH) — 돌려받은 함수를 부른다
 *
 * 통계용이라 실패해도 화면에는 아무 일도 없어야 한다. 그래서 오류 처리(토스트·세션 종료)가
 * 붙은 useFetcher 를 쓰지 않고 client 로 던지고 잊는다.
 */
export default function useLogOrderSheetView({
  buyerId,
  orderSheetSetId,
  view,
  isEnabled,
}: UseLogOrderSheetViewParameter) {
  const send = useCallback(
    (targetView: OrderSheetView, trigger: OrderSheetViewTrigger) => {
      if (buyerId === undefined || orderSheetSetId < 0) {
        return;
      }
      client
        .post(
          calcUrl(logOrderSheetViewTemplateUrl, { buyerId, orderSheetSetId }),
          {
            view: targetView,
            trigger,
          }
        )
        .catch(() => {
          // 기록 실패는 무시한다
        });
    },
    [buyerId, orderSheetSetId]
  );

  // 보기를 바꿔도 OPEN 을 다시 보내지 않게 최신 값은 ref 로만 읽는다
  const viewRef = useRef(view);
  viewRef.current = view;
  // 주문서마다 한 번 — 데이터 재검증이나 개발 모드의 effect 재실행으로 겹쳐 남지 않게
  const openedKeyRef = useRef<string>();

  useEffect(() => {
    if (!isEnabled || buyerId === undefined || orderSheetSetId < 0) {
      return;
    }
    const key = `${buyerId}:${orderSheetSetId}`;
    if (openedKeyRef.current === key) {
      return;
    }
    openedKeyRef.current = key;
    send(viewRef.current, "OPEN");
  }, [isEnabled, buyerId, orderSheetSetId, send]);

  return useCallback(
    (nextView: OrderSheetView) => send(nextView, "SWITCH"),
    [send]
  );
}
